/**
 * DFW Professionals composite scoring model.
 *
 * This module is the single source of truth for how businesses are ranked.
 * The public /scoring-method/ page renders directly from the constants and
 * descriptions exported here, so the published methodology can never drift
 * from the code that actually produces the scores.
 *
 * Isomorphic on purpose: no Prisma, no Node built-ins, no Trigger imports.
 * Imported by both the scoring task and the marketing route.
 *
 * See docs/directory-rebuild-plan.md §5
 */

import { classifyAnalysisState } from '#app/ai/review-synthesis.ts'

// ---------------------------------------------------------------------------
// Weights
// ---------------------------------------------------------------------------

export const SCORE_FACTORS = [
	{
		key: 'sentiment',
		label: 'Review sentiment',
		weight: 0.24,
		description:
			'What reviewers actually say, not just the star they picked. Reviews are read for recurring themes such as punctuality, cleanup, pricing transparency and callback responsiveness, then scored on the balance of positive to negative language.',
	},
	{
		key: 'rating',
		label: 'Star rating',
		weight: 0.22,
		description:
			'Google star rating, adjusted so a 5.0 from three reviews does not outrank a 4.7 from four hundred. We pull ratings toward the category average until enough reviews exist to trust the number, then discount businesses carrying an unusual share of one and two star reviews behind a high average.',
	},
	{
		key: 'volume',
		label: 'Review volume',
		weight: 0.14,
		description:
			'How many people have weighed in, on a diminishing scale. The jump from 10 reviews to 100 counts for far more than the jump from 400 to 500.',
	},
	{
		key: 'trajectory',
		label: 'Direction of travel',
		weight: 0.14,
		description:
			'Whether the business is getting better or worse. We compare the ratings left over the last year against the year before it. A company on the way up and a company on the way down can sit at the same average, and that average is the last thing to move.',
	},
	{
		key: 'recency',
		label: 'Recency',
		weight: 0.1,
		description:
			'How recent the reviews are. A business that was excellent in 2019 and silent since is not the same bet as one with steady feedback this year.',
	},
	{
		key: 'responsiveness',
		label: 'Response to complaints',
		weight: 0.1,
		description:
			'How often the owner publicly replies to reviews, weighted heavily toward the critical ones. Anyone can thank a five star review. Answering the customer who says the work went wrong is what shows you how a business responds when something goes wrong for you.',
	},
	{
		key: 'completeness',
		label: 'Profile completeness',
		weight: 0.06,
		description:
			'Whether the business publishes the basics: working website, listed hours, service details, photos. Incomplete profiles are harder to verify and score lower.',
	},
] as const

export type ScoreFactorKey = (typeof SCORE_FACTORS)[number]['key']

/** Weights must sum to 1. Guarded at module load so a bad edit fails loudly. */
const WEIGHT_SUM = SCORE_FACTORS.reduce((sum, f) => sum + f.weight, 0)
if (Math.abs(WEIGHT_SUM - 1) > 1e-9) {
	throw new Error(
		`SCORE_FACTORS weights must sum to 1, got ${WEIGHT_SUM}. Update SCORE_FACTORS in app/scoring/scoring-model.ts.`,
	)
}

// ---------------------------------------------------------------------------
// Tunable constants (also rendered on /scoring-method/)
// ---------------------------------------------------------------------------

/** Bayesian prior strength — reviews needed before a rating is taken at face value. */
export const BAYESIAN_PRIOR_VOTES = 25
/** Baseline rating a business is pulled toward when review count is low. */
export const BAYESIAN_PRIOR_RATING = 3.8
/** Review count at which the volume factor saturates. */
export const VOLUME_SATURATION = 250
/** Reviews newer than this are treated as fully current. */
export const RECENCY_FULL_CREDIT_DAYS = 180
/** Reviews older than this contribute nothing to the recency factor. */
export const RECENCY_ZERO_CREDIT_DAYS = 1095 // 3 years

/** Minimum reviews before we publish a score at all. */
export const MIN_REVIEWS_TO_SCORE = 5

/**
 * What an unmeasured factor contributes to the composite.
 *
 * Neutral, not zero and not redistributed. See the note in computeScore for
 * why redistribution is an optimistic imputation rather than a neutral one.
 */
export const UNKNOWN_FACTOR_VALUE = 0.5

/** Length of each trajectory comparison window. Recent year vs the year before. */
export const TRAJECTORY_WINDOW_DAYS = 365
/**
 * Reviews required in *both* windows before a direction is claimed.
 *
 * Below this the comparison is noise, and "this company is declining" is a
 * serious thing to publish about a real business on the strength of two
 * reviews.
 */
export const TRAJECTORY_MIN_PER_WINDOW = 5
/**
 * Star-average swing that counts as a full move in either direction. A half
 * star sustained across a year is a large shift in a category where almost
 * everyone sits above 4.5.
 */
export const TRAJECTORY_SATURATION_DELTA = 0.5
/** Swing below this is reported as steady rather than a direction. */
export const TRAJECTORY_FLAT_DELTA = 0.1

/** Reviews at or below this rating are treated as critical. */
export const CRITICAL_RATING_MAX = 3
/** Held reviews required before a response rate means anything. */
export const RESPONSIVENESS_MIN_REVIEWS = 10
/** Critical reviews required before they are scored separately. */
export const RESPONSIVENESS_MIN_CRITICAL = 3
/** Share of the responsiveness factor carried by replies to critical reviews. */
export const CRITICAL_RESPONSE_SHARE = 0.65

/**
 * Share of one and two star reviews treated as normal. Every business with
 * real volume has some.
 */
export const POLARIZATION_TOLERANCE = 0.05
/** Excess low-star share at which the rating penalty maxes out. */
export const POLARIZATION_SATURATION = 0.1
/**
 * Most the rating factor can be cut for polarization. Capped because the low
 * stars are already counted once in the average and in sentiment; this exists
 * to stop a high average hiding a pattern, not to punish twice.
 */
export const POLARIZATION_MAX_PENALTY = 0.15
/** Reviews in the distribution before polarization is measured at all. */
export const POLARIZATION_MIN_REVIEWS = 20

/**
 * Re-exported for /scoring-method/ so the public page can state the citation
 * requirement without importing the synthesis module (which pulls in zod and
 * prompt text the marketing route has no use for). Kept in sync by a test.
 */
export const MIN_CITATIONS_PER_CLAIM_PUBLIC = 2

// ---------------------------------------------------------------------------
// Inputs / outputs
// ---------------------------------------------------------------------------

/** One held review, reduced to the fields scoring actually reads. */
export interface ScoreReview {
	timestamp?: Date | string | null
	rating?: number | null
	/** The owner's public reply, when there is one. */
	ownerAnswer?: string | null
}

/**
 * Google's `rating_distribution`, keyed by star value.
 *
 * Typed loosely because it arrives as `Json` from Prisma and the keys come
 * back as strings. `parseRatingDistribution` is the only thing that reads it.
 */
export type RatingDistribution = Record<string | number, unknown>

export interface ScoreInput {
	/** Google star rating, 0–5. */
	ratingValue?: number | null
	/** Google review count. */
	ratingCount?: number | null
	/** Aggregate sentiment across the review corpus, -1..1. */
	sentimentScore?: number | null
	/** Star histogram from Google, used to detect polarization. */
	ratingDist?: RatingDistribution | null
	/**
	 * The reviews we hold. Drives recency, trajectory and responsiveness.
	 *
	 * Absent before post-review-tasks has run for a business, which is the
	 * normal state during preliminary scoring. Every factor that needs it
	 * returns null in that case rather than zero.
	 */
	reviews?: ScoreReview[]
	/**
	 * Timestamps only, used when the caller has dates but not full reviews.
	 * Ignored when `reviews` is present. Feeds recency and nothing else.
	 */
	reviewDates?: Array<Date | string | null | undefined>
	/** Profile completeness signals. */
	hasWebsite?: boolean
	hasHours?: boolean
	hasPhotos?: boolean
	hasDescription?: boolean
	hasPhone?: boolean
	/** Reference date; injected so scoring is deterministic and testable. */
	now?: Date
}

/**
 * Every factor that depends on the review corpus is nullable, meaning "we
 * have not measured this yet" rather than "this business scored zero".
 * computeScore feeds anything null into the composite at UNKNOWN_FACTOR_VALUE
 * and leaves it null here, so a page can say "not measured" without the score
 * pretending we measured it. The weight is never redistributed — see the note
 * in computeScore.
 */
export interface ScoreBreakdown {
	sentiment: number
	rating: number
	volume: number
	/** null when we hold no dated reviews — unknown rather than stale. */
	recency: number | null
	/** null until both comparison windows hold enough reviews. */
	trajectory: number | null
	/** null until we hold enough reviews for a response rate to mean anything. */
	responsiveness: number | null
	completeness: number
}

/** The raw year-over-year comparison, kept for page copy. */
export interface ReviewTrajectory {
	/** Recent-year mean rating minus prior-year mean rating. */
	delta: number
	recentMean: number
	priorMean: number
	recentCount: number
	priorCount: number
	direction: 'improving' | 'steady' | 'declining'
}

export interface ScoreResult {
	/** Composite score, 0–100, rounded to one decimal. */
	score: number
	/** Per-factor sub-scores, each 0–100. */
	breakdown: ScoreBreakdown
	/**
	 * Year-over-year detail behind the trajectory factor, so a page can say
	 * "4.4 to 4.8 over the last year" instead of printing a sub-score. Null
	 * whenever the trajectory factor is null.
	 */
	trajectoryDetail: ReviewTrajectory | null
	/** False when the corpus is too thin to publish a score. */
	isPublishable: boolean
	/** How confident we are in the inputs. Rendered next to the score. */
	confidence: 'high' | 'medium' | 'low'
}

// ---------------------------------------------------------------------------
// Factor calculations
// ---------------------------------------------------------------------------

const clamp01 = (n: number) => Math.min(1, Math.max(0, n))

const toDate = (value: Date | string | null | undefined): Date | null => {
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
	if (!value) return null
	const parsed = new Date(value)
	return Number.isNaN(parsed.getTime()) ? null : parsed
}

const mean = (values: number[]) =>
	values.reduce((sum, n) => sum + n, 0) / values.length

/**
 * Google's star histogram as counts per star, or null when it is unusable.
 *
 * Tolerant on purpose: the field arrives as untyped `Json`, keys may be
 * strings or numbers, and counts have been seen as numeric strings.
 */
export function parseRatingDistribution(
	dist?: RatingDistribution | null,
): Record<1 | 2 | 3 | 4 | 5, number> | null {
	if (!dist || typeof dist !== 'object' || Array.isArray(dist)) return null

	const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
	let sawAny = false

	for (const star of [1, 2, 3, 4, 5] as const) {
		const raw = (dist as Record<string, unknown>)[String(star)]
		const value = typeof raw === 'string' ? Number(raw) : raw
		if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
			continue
		}
		counts[star] = value
		sawAny = true
	}

	return sawAny ? counts : null
}

/**
 * How much the rating factor is cut for an unusual share of one and two star
 * reviews, 0–POLARIZATION_MAX_PENALTY.
 *
 * A 4.8 built from a smooth curve and a 4.8 built from a wall of fives plus a
 * seam of ones are different businesses, and the second one is where the
 * honest watch-outs come from. Returns 0 when the histogram is missing or too
 * thin, so a business is never penalized for data we do not hold.
 */
export function polarizationPenalty(dist?: RatingDistribution | null): number {
	const counts = parseRatingDistribution(dist)
	if (!counts) return 0

	const total = counts[1] + counts[2] + counts[3] + counts[4] + counts[5]
	if (total < POLARIZATION_MIN_REVIEWS) return 0

	const lowShare = (counts[1] + counts[2]) / total
	const excess = Math.max(0, lowShare - POLARIZATION_TOLERANCE)

	return Math.min(
		POLARIZATION_MAX_PENALTY,
		(excess / POLARIZATION_SATURATION) * POLARIZATION_MAX_PENALTY,
	)
}

/**
 * Bayesian-weighted rating, normalized to 0–1, discounted for polarization.
 * Formula: (v / (v + m)) * R + (m / (v + m)) * C
 * Preserves the existing behaviour in business-ranking.ts.
 */
export function ratingFactor(
	ratingValue?: number | null,
	ratingCount?: number | null,
	ratingDist?: RatingDistribution | null,
): number {
	const R = ratingValue ?? 0
	const v = ratingCount ?? 0
	const m = BAYESIAN_PRIOR_VOTES
	const C = BAYESIAN_PRIOR_RATING
	if (v + m === 0) return 0
	const weighted = (v / (v + m)) * R + (m / (v + m)) * C
	return clamp01((weighted / 5) * (1 - polarizationPenalty(ratingDist)))
}

/**
 * Log-scaled review volume, normalized to 0–1.
 * Saturates at VOLUME_SATURATION so a 2,000-review chain cannot bury a
 * well-reviewed independent on volume alone.
 */
export function volumeFactor(ratingCount?: number | null): number {
	const v = Math.max(0, ratingCount ?? 0)
	if (v === 0) return 0
	return clamp01(Math.log1p(v) / Math.log1p(VOLUME_SATURATION))
}

/**
 * Sentiment mapped from -1..1 onto 0–1.
 *
 * Null (not yet synthesized) falls back to UNKNOWN_FACTOR_VALUE rather than
 * zero, so an un-synthesized business is not silently penalized. This is the
 * same policy computeScore applies to every other unmeasured factor; it is
 * spelled out here because sentiment resolves its own unknown rather than
 * returning null.
 */
export function sentimentFactor(sentimentScore?: number | null): number {
	if (sentimentScore == null || !Number.isFinite(sentimentScore)) {
		return UNKNOWN_FACTOR_VALUE
	}
	return clamp01((sentimentScore + 1) / 2)
}

/**
 * Recency from the review date distribution, 0–1.
 * Uses the median review age so a single recent review cannot mask years
 * of silence.
 *
 * Returns `null` when we hold no dated reviews. That is "unknown", not
 * "stale" — before fetch-business-reviews runs, every business would otherwise
 * take the full recency penalty for data we simply have not pulled yet.
 * computeScore scores it neutral instead of zero, without redistributing its
 * weight.
 */
export function recencyFactor(
	reviewDates?: Array<Date | string | null | undefined>,
	now: Date = new Date(),
): number | null {
	const times = (reviewDates ?? [])
		.map((d) => (d instanceof Date ? d : d ? new Date(d) : null))
		.filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()))
		.map((d) => d.getTime())
		.sort((a, b) => a - b)

	if (times.length === 0) return null

	const mid = Math.floor(times.length / 2)
	const medianTime =
		times.length % 2 === 0
			? ((times[mid - 1] as number) + (times[mid] as number)) / 2
			: (times[mid] as number)

	const ageDays = (now.getTime() - medianTime) / 86_400_000
	if (ageDays <= RECENCY_FULL_CREDIT_DAYS) return 1
	if (ageDays >= RECENCY_ZERO_CREDIT_DAYS) return 0

	const span = RECENCY_ZERO_CREDIT_DAYS - RECENCY_FULL_CREDIT_DAYS
	return clamp01(1 - (ageDays - RECENCY_FULL_CREDIT_DAYS) / span)
}

/** Profile completeness, 0–1. Equal weight across five published signals. */
export function completenessFactor(input: ScoreInput): number {
	const signals = [
		input.hasWebsite,
		input.hasHours,
		input.hasPhotos,
		input.hasDescription,
		input.hasPhone,
	]
	const present = signals.filter(Boolean).length
	return clamp01(present / signals.length)
}

/**
 * Year-over-year change in the ratings people are leaving, or null when we
 * cannot see far enough back to say.
 *
 * The star average is a lagging indicator: a business with 400 reviews at 4.8
 * can spend a year shipping bad work and barely move, because every new
 * review is diluted by four hundred old ones. Comparing the last twelve
 * months against the twelve before it is what surfaces that while it still
 * matters to the person reading the page.
 *
 * Returns null unless both windows clear TRAJECTORY_MIN_PER_WINDOW. That is
 * the common case for businesses whose held corpus only goes back a year, and
 * it is the right answer: "we cannot tell" rather than a direction invented
 * from three reviews.
 */
export function reviewTrajectory(
	reviews?: ScoreReview[],
	now: Date = new Date(),
): ReviewTrajectory | null {
	const windowMs = TRAJECTORY_WINDOW_DAYS * 86_400_000
	const recentCutoff = now.getTime() - windowMs
	const priorCutoff = now.getTime() - windowMs * 2

	const recent: number[] = []
	const prior: number[] = []

	for (const review of reviews ?? []) {
		const date = toDate(review.timestamp)
		const rating = review.rating
		if (!date || typeof rating !== 'number' || !Number.isFinite(rating)) {
			continue
		}
		const time = date.getTime()
		// Reviews dated in the future are clock skew on someone else's system.
		if (time > now.getTime()) continue
		if (time >= recentCutoff) recent.push(rating)
		else if (time >= priorCutoff) prior.push(rating)
	}

	if (
		recent.length < TRAJECTORY_MIN_PER_WINDOW ||
		prior.length < TRAJECTORY_MIN_PER_WINDOW
	) {
		return null
	}

	const recentMean = mean(recent)
	const priorMean = mean(prior)
	const delta = recentMean - priorMean

	return {
		delta,
		recentMean,
		priorMean,
		recentCount: recent.length,
		priorCount: prior.length,
		direction:
			delta > TRAJECTORY_FLAT_DELTA
				? 'improving'
				: delta < -TRAJECTORY_FLAT_DELTA
					? 'declining'
					: 'steady',
	}
}

/**
 * Trajectory as a 0–1 factor, with the midpoint meaning flat.
 *
 * Steady sits at the midpoint deliberately. A business holding a good rating
 * for three years is not underperforming, and scoring stability as a failure
 * would hand the top of every ranking to whoever had the worst prior year.
 *
 * The midpoint is UNKNOWN_FACTOR_VALUE rather than a separate literal, which
 * pins "steady" and "we could not measure a direction" to the same number.
 * That equality is the claim the factor makes when the windows are too thin,
 * so it should not be possible to change one without the other.
 */
export function trajectoryFactor(
	reviews?: ScoreReview[],
	now: Date = new Date(),
): number | null {
	const trajectory = reviewTrajectory(reviews, now)
	if (!trajectory) return null
	return clamp01(
		UNKNOWN_FACTOR_VALUE + trajectory.delta / (2 * TRAJECTORY_SATURATION_DELTA),
	)
}

/**
 * How often the owner replies publicly, weighted toward critical reviews.
 *
 * Replies to five star reviews are close to free and largely automated, so
 * they carry the smaller share. Replying to the customer who says the job
 * went wrong is the behaviour a reader is actually trying to predict, and it
 * is the one thing on the page that describes what happens when their own job
 * goes wrong.
 *
 * Returns null below RESPONSIVENESS_MIN_REVIEWS. A business with six held
 * reviews and no replies has not demonstrated anything.
 */
export function responsivenessFactor(reviews?: ScoreReview[]): number | null {
	const held = reviews ?? []
	if (held.length < RESPONSIVENESS_MIN_REVIEWS) return null

	const answered = (list: ScoreReview[]) =>
		list.filter((r) => Boolean(r.ownerAnswer?.trim())).length

	const overallRate = answered(held) / held.length

	const critical = held.filter(
		(r) => typeof r.rating === 'number' && r.rating <= CRITICAL_RATING_MAX,
	)

	// No critical reviews is not a failure to respond to them, so the overall
	// rate stands on its own rather than scoring an empty set as zero.
	if (critical.length < RESPONSIVENESS_MIN_CRITICAL) {
		return clamp01(overallRate)
	}

	const criticalRate = answered(critical) / critical.length

	return clamp01(
		criticalRate * CRITICAL_RESPONSE_SHARE +
			overallRate * (1 - CRITICAL_RESPONSE_SHARE),
	)
}

// ---------------------------------------------------------------------------
// Composite
// ---------------------------------------------------------------------------

/**
 * Compute the published 0–100 score and its per-factor breakdown.
 * Pure and deterministic — pass `now` to pin the recency curve in tests.
 */
export function computeScore(input: ScoreInput): ScoreResult {
	const now = input.now ?? new Date()

	// `reviews` is the real corpus; `reviewDates` is the dates-only fallback for
	// callers that hold timestamps but not full review rows. Recency is the only
	// factor the fallback can serve.
	const reviewDates =
		input.reviews?.map((r) => r.timestamp) ?? input.reviewDates ?? []

	const recency = recencyFactor(reviewDates, now)
	const trajectoryDetail = reviewTrajectory(input.reviews, now)
	const trajectory = trajectoryFactor(input.reviews, now)
	const responsiveness = responsivenessFactor(input.reviews)

	const factors = {
		sentiment: sentimentFactor(input.sentimentScore),
		rating: ratingFactor(
			input.ratingValue,
			input.ratingCount,
			input.ratingDist,
		),
		volume: volumeFactor(input.ratingCount),
		// The nullable three mean "not measured yet". They enter the composite at
		// UNKNOWN_FACTOR_VALUE and stay null in the breakdown, so the page can say
		// "not measured" without the score pretending we measured it.
		recency,
		trajectory,
		responsiveness,
		completeness: completenessFactor(input),
	}

	/*
	 * Unknown scores neutral, the same policy sentimentFactor already uses.
	 *
	 * The obvious alternative is to drop unmeasured factors and renormalize over
	 * the rest. That reads as fair and is not: redistributing a factor's weight
	 * across the remaining ones imputes the business's own average to it. For a
	 * 4.9-star listing whose other factors sit near 0.9, skipping trajectory and
	 * responsiveness is worth roughly 0.9 on both, while businesses we actually
	 * measured average near 0.5 and 0.63. Fetching a review corpus became a
	 * ~6-point penalty, which is exactly backwards: it marked down the listings
	 * we know the most about.
	 *
	 * Neutral also states the right thing. 0.5 is already what trajectory means
	 * by "steady", so an unmeasurable direction and a flat one land in the same
	 * place, which is the honest claim. And it finally separates the two cases
	 * responsiveness exists to distinguish: never checked scores 0.5, a real
	 * corpus with a silent owner scores 0.
	 *
	 * Weights sum to 1, so no renormalization is needed or wanted.
	 */
	const composite = SCORE_FACTORS.reduce(
		(sum, factor) =>
			sum + (factors[factor.key] ?? UNKNOWN_FACTOR_VALUE) * factor.weight,
		0,
	)

	const round1 = (n: number) => Math.round(n * 1000) / 10

	const reviewCount = input.ratingCount ?? 0
	const hasSentiment = input.sentimentScore != null
	const hasDates = reviewDates.length > 0

	// Deliberately unchanged by the new factors. Confidence answers "how much
	// review data backs this score", and trajectory and responsiveness are
	// unmeasurable for reasons (a short held corpus) that say nothing about
	// whether the score itself is well supported.
	const confidence: ScoreResult['confidence'] =
		reviewCount >= 30 && hasSentiment && hasDates
			? 'high'
			: reviewCount >= MIN_REVIEWS_TO_SCORE && hasSentiment
				? 'medium'
				: 'low'

	return {
		score: round1(composite),
		breakdown: {
			sentiment: round1(factors.sentiment),
			rating: round1(factors.rating),
			volume: round1(factors.volume),
			recency: recency == null ? null : round1(recency),
			trajectory: trajectory == null ? null : round1(trajectory),
			responsiveness: responsiveness == null ? null : round1(responsiveness),
			completeness: round1(factors.completeness),
		},
		trajectoryDetail,
		isPublishable: reviewCount >= MIN_REVIEWS_TO_SCORE,
		confidence,
	}
}

// ---------------------------------------------------------------------------
// Depth floors — how many listings a page needs before it may be built.
// Enforced by the sitemap and the page loaders. See plan §2.
// ---------------------------------------------------------------------------

export const DEPTH_FLOORS = {
	/** /{city}/{category} — the existing indexed local ranking URL. */
	cityCategory: 5,
	/** /best/{category}/ */
	bestMetro: 10,
	/*
	 * There is deliberately no bestCity floor. City rankings live at
	 * /{city}/{category}, the URL that already exists and is already indexed,
	 * rather than a second /best/{category}/{city} surface competing for the
	 * same query. See the header of that route.
	 */
	/** /compare/{a}-vs-{b}/ — max score gap for a fair comparison. */
	compareMaxScoreGap: 25,
} as const

// ---------------------------------------------------------------------------
// Corpus coverage — how much of a ranking has to be measured, not just scored.
// ---------------------------------------------------------------------------

/**
 * Held reviews before a business counts as measured.
 *
 * Tied to RESPONSIVENESS_MIN_REVIEWS because responsiveness is the only corpus
 * factor gated on held count alone, and it sets the highest such bar. Below
 * this number responsiveness is guaranteed to be falling back to neutral.
 *
 * The other three are not promised in either direction, and this constant
 * should not be read as speaking for them:
 *
 * - Recency resolves from any dated review at all, so it is usually measured
 *   well below this bar.
 * - Sentiment follows synthesis, not held count. A business can hold plenty of
 *   reviews and still have no sentiment because it has not been synthesized.
 * - Trajectory needs TRAJECTORY_MIN_PER_WINDOW reviews in *each* of two
 *   year-long windows. That is a distribution requirement no single count can
 *   express: ten reviews all left in the last year clear this constant and
 *   still leave trajectory unmeasured.
 *
 * So this is a proxy for "we have a real corpus for this listing", chosen
 * because it is cheap to count and correlates with the rest. Coverage built on
 * it is a floor on how much of a ranking is real, never a guarantee that any
 * individual listing is fully measured.
 */
export const CORPUS_MIN_REVIEWS = RESPONSIVENESS_MIN_REVIEWS

/**
 * Share of a ranking's listings that must be measured before the page may be
 * published.
 *
 * A depth floor counts listings; it cannot tell whether the ordering means
 * anything. In a category where no reviews have been fetched, 58% of the
 * model's weight (sentiment, trajectory, recency, responsiveness) sits at
 * UNKNOWN_FACTOR_VALUE for every business, so the ranking is decided entirely
 * by Bayesian rating, volume and completeness while presenting itself as the
 * full seven-factor score. That page clears every depth floor and is still
 * flat.
 *
 * 60% is deliberately not 100%: a ranking where most listings are measured is
 * a real ordering, and the long tail below the fold does not have to be paid
 * for before the page is worth publishing.
 */
export const CORPUS_COVERAGE_FLOOR = 0.6

/**
 * Businesses shown in a ranked list.
 *
 * Lives here rather than in each route because the coverage floor is measured
 * across exactly this slice, so the sitemap has to agree with the pages about
 * how long a ranking is.
 */
export const RANK_LIMIT = 15

/**
 * The one total order every ranking uses: score, then id.
 *
 * Scores tie often (hundreds of distinct values are shared), and the coverage
 * floor is measured over the top RANK_LIMIT, so which tied business lands at
 * position 15 can decide whether a page publishes. Ordering by score alone
 * left that to Postgres and to Prisma's return order, so the sitemap inventory
 * and the page loader could disagree about the same population. Every caller
 * that ranks — the SQL inventory, the loaders and the helpers below — uses
 * this order so they cannot.
 *
 * Ids are lowercase alphanumeric cuids, which sort identically under the
 * database's en_US collation and JavaScript's code-unit comparison.
 */
export const RANK_ORDER = [{ score: 'desc' }, { id: 'asc' }] as const

export function compareRankOrder(
	a: { id: string; score: number | null },
	b: { id: string; score: number | null },
): number {
	const byScore = (b.score ?? 0) - (a.score ?? 0)
	if (byScore !== 0) return byScore
	return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/**
 * Whether a ranking page may be built: enough listings, and enough of the ones
 * a reader will actually see actually measured.
 *
 * Coverage is deliberately measured over the top RANK_LIMIT by score, not over
 * every eligible business in the category. Review spend is tiered (plan §4.2):
 * we buy review text for the businesses that will appear on a ranked page and
 * not for the long tail below the fold. Measuring coverage across the whole
 * eligible pool would demand we pay for that tail before any page could
 * publish, which inverts the tiering it is supposed to protect. Measured on the
 * roofing pilot, whole-pool coverage is 26% while the ranked slice is ~95%.
 *
 * Pure and isomorphic so the sitemap, the /best/ loader and the city loader
 * share one predicate. They previously each restated the depth check, which is
 * exactly how a sitemap starts advertising URLs its loader redirects.
 */
export function clearsRankingFloor(input: {
	/** Ranking-eligible businesses in the category, however many. */
	total: number
	/**
	 * How many of the top RANK_LIMIT by score hold at least CORPUS_MIN_REVIEWS
	 * reviews. Callers must count over the ranked slice, not the whole pool.
	 */
	measuredInRankedSlice: number
	/** Depth floor for this page type, from DEPTH_FLOORS. */
	depthFloor: number
}): boolean {
	if (input.total === 0) return false
	if (input.total < input.depthFloor) return false

	const rankedSize = Math.min(input.total, RANK_LIMIT)

	/*
	 * A caller that counted over the whole pool instead of the ranked slice
	 * would report more measured businesses than there are slots, and silently
	 * publish everything at >100% coverage. That is the exact bug this
	 * function's first version had, so it fails loudly rather than trusting the
	 * count.
	 */
	if (input.measuredInRankedSlice > rankedSize) {
		throw new Error(
			`clearsRankingFloor: measuredInRankedSlice (${input.measuredInRankedSlice}) exceeds the ranked slice (${rankedSize}). Count over the top ${RANK_LIMIT} by score, not the whole eligible pool.`,
		)
	}

	return input.measuredInRankedSlice / rankedSize >= CORPUS_COVERAGE_FLOOR
}

/**
 * Whether a business profile may be indexed.
 *
 * The sibling of `clearsRankingFloor`, and pure for the same reason: the
 * sitemap and the profile loader must not restate this separately, or the
 * sitemap starts advertising URLs the page tells Google to drop.
 *
 * Indexable means the page has publishable editorial ON IT, now. Not that it
 * ranks, not that a corpus was bought, not that the analysis ran — that it
 * says something. So this delegates to `classifyAnalysisState` rather than
 * deciding for itself, because that function is already the codebase's
 * definition of "has something to show", and a second opinion here is exactly
 * the drift `clearsRankingFloor` exists to prevent.
 *
 * Two near-misses this deliberately excludes, both measured on production:
 *
 *  - `no_claims` (241 profiles). `synthesisAt` is stamped even when every
 *    claim failed grounding validation, so the timestamp cannot stand in for
 *    content. Indexing on it published 240 pages with nothing written on them.
 *  - `no_corpus` (3,952 profiles). Google reporting five reviews says a corpus
 *    could be bought, not that it was. "Might eventually have content" is a
 *    backfill criterion, and it belongs in the backfill task — see
 *    `backfill-review-corpus`, which selects on `ratingCount` for exactly that
 *    reason. Indexing on it here published a further 2,739 empty pages, which
 *    is the scaled-content pattern this floor exists to end.
 *
 * Self-healing in both directions, and that is what makes the strict reading
 * affordable: buy the corpus, synthesis publishes claims, and the same
 * predicate puts the page back in the sitemap with no human in the loop.
 */
export function clearsProfileFloor(input: {
	reviewsHeld: number | null
	synthesisAt: Date | string | null
	pros: string[]
	watchOuts: string[]
}): boolean {
	return classifyAnalysisState(input) === 'published'
}
